// src/pages/Tickets/TicketDetail.jsx
// -----------------------------------------------------------------------------
// Full detail view of a single ticket. Covers:
//   - Requirement #13: all core ticket fields
//   - Requirement #14: full status history
//   - Requirement #15/#19: comments (+ internal vs client-visible)
//   - Requirement #19/#20: assignment history / multiple assignees
//   - Attachments (Ticket_Attachment_TBL)
// -----------------------------------------------------------------------------
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ticketService from '../../api/ticketService';
import userService from '../../api/userService';
import projectService from '../../api/projectService';
import ticketStatusService from '../../api/ticketStatusService';
import ticketSeverityService from '../../api/ticketSeverityService';
import ticketTypeService from '../../api/ticketTypeService';
import projectEnvironmentService from '../../api/projectEnvironmentService';
import clientOrgService from '../../api/clientOrgService';
import ticketReopenReasonService from '../../api/ticketReopenReasonService';
import Loader from '../../components/common/Loader';
import StatusBadge from '../../components/common/StatusBadge';
import Modal from '../../components/common/Modal';
import TicketLinksPanel from './TicketLinksPanel';
import TicketClientAnswers from './TicketClientAnswers';
import { useAuth } from '../../context/AuthContext';
import './tickets.css';

const TABS = ['Comments', 'History', 'Attachments', 'Links'];

// Keep in sync with the "Attachments" section of the API's appsettings.json
// (the API checks again and has the final say).
const MAX_ATTACHMENT_MB = 10;
const ALLOWED_ATTACHMENT_EXTENSIONS = [
  '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.pdf', '.txt', '.log', '.csv',
  '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.zip', '.mp4', '.mov',
];

const isImageFile = (fileName, fileType) => {
  const type = String(fileType || '').toLowerCase();
  const name = String(fileName || '').toLowerCase();
  return type.startsWith('image/') ||
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg') ||
    name.endsWith('.png') ||
    name.endsWith('.gif') ||
    name.endsWith('.svg') ||
    name.endsWith('.webp');
};

const getAttachmentUrl = (filePath) => {
  if (!filePath) return '';
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) return filePath;
  const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:5020';
  const origin = apiBase.replace(/\/api\/?$/, ''); // Strip '/api' from the end
  return `${origin}${filePath}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return dateStr;
  }
};

// Helper to extract full user name from any user object shape
const getUserFullName = (u) => {
  if (!u) return '';
  if (typeof u === 'string' || typeof u === 'number') return String(u);
  const first = (u.first_Name || u.firstName || u.First_Name || '').trim();
  const last = (u.last_Name || u.lastName || u.Last_Name || '').trim();
  const full = `${first} ${last}`.trim();
  if (full) return full;
  if (u.fullName || u.name || u.userName || u.user_Name) {
    return (u.fullName || u.name || u.userName || u.user_Name).trim();
  }
  if (u.email || u.Email) return u.email || u.Email;
  const uid = u.id ?? u.userID ?? u.userId ?? u.Id ?? u.UserId;
  return uid ? `User ${uid}` : 'User';
};

// Helper to extract User ID
const getUserId = (u) => {
  if (!u) return '';
  return u.id ?? u.userID ?? u.userId ?? u.Id ?? u.UserId ?? '';
};

export default function TicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Comments');

  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const [newComment, setNewComment] = useState('');
  const [isInternalComment, setIsInternalComment] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [changingStatus, setChangingStatus] = useState(false);
  const [reopenReasons, setReopenReasons] = useState([]);
  // Status change dialog: { toStatusID, isReopen } - collects notes / reopen reason
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusComment, setStatusComment] = useState('');
  const [statusReopenReasonID, setStatusReopenReasonID] = useState('');

  // Ticket Assignment State
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedAssignUserId, setSelectedAssignUserId] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Helper to map and resolve assignment objects with user full names
  const resolveAssignmentList = (rawAssignments, userList) => {
    return (rawAssignments || []).map((a) => {
      const uid = a.userID || a.userId;
      const assignee = (userList || []).find((u) => Number(getUserId(u)) === Number(uid));
      const uname = assignee
        ? getUserFullName(assignee)
        : (a.userName && !a.userName.startsWith('User ') ? a.userName : (uid ? `User ${uid}` : ''));
      return {
        ...a,
        userID: uid,
        userName: uname,
      };
    });
  };

  // Handle Assigning Ticket to User
  const handleAssignUser = async (e) => {
    if (e) e.preventDefault();
    if (!selectedAssignUserId) {
      toast.warning('Please select a user to assign.');
      return;
    }

    const isAlreadyAssigned = assignments.some(
      (a) => a.isCurrentFlag && Number(a.userID || a.userId) === Number(selectedAssignUserId)
    );
    if (isAlreadyAssigned) {
      toast.warning('This user is already assigned to this ticket.');
      return;
    }

    setAssigning(true);
    try {
      // Find real user ID from users list for the logged-in assigner
      let loggedInId = 1;
      const matchedLoggedInUser = (users || []).find(
        (u) => u.email && user?.email && u.email.trim().toLowerCase() === user.email.trim().toLowerCase()
      );
      if (matchedLoggedInUser) {
        loggedInId = Number(getUserId(matchedLoggedInUser));
      } else {
        const rawId = user?.userID || user?.userId || user?.id;
        const matchedById = (users || []).find((u) => Number(getUserId(u)) === Number(rawId));
        if (matchedById) {
          loggedInId = Number(getUserId(matchedById));
        } else if (users.length > 0) {
          loggedInId = Number(getUserId(users[0]));
        } else {
          loggedInId = Number(rawId || 1);
        }
      }

      await ticketService.assign(ticketId, selectedAssignUserId, loggedInId);
      const res = await ticketService.getAssignments(ticketId);
      const updated = resolveAssignmentList(res.data || [], users);
      setAssignments(updated);
      toast.success('Ticket assigned successfully!');
      setIsAssignModalOpen(false);
      setSelectedAssignUserId('');
    } catch (err) {
      console.error('Failed to assign ticket:', err);
      const errMsg = err?.response?.data?.message || err?.response?.data?.title || err?.message || 'Failed to assign ticket.';
      toast.error(errMsg);
    } finally {
      setAssigning(false);
    }
  };

  // Handle Unassigning User from Ticket
  const handleUnassignUser = async (assignmentID) => {
    if (!window.confirm('Are you sure you want to unassign this user?')) return;
    try {
      await ticketService.unassign(ticketId, assignmentID);
      const res = await ticketService.getAssignments(ticketId);
      const updated = resolveAssignmentList(res.data || [], users);
      setAssignments(updated);
      toast.success('User unassigned successfully.');
    } catch (err) {
      console.error('Failed to unassign user:', err);
      const errMsg = err?.response?.data?.message || err?.response?.data?.title || err?.message || 'Failed to unassign user.';
      toast.error(errMsg);
    }
  };

  // Choosing a new status opens a small dialog for notes (e.g. resolution
  // notes) and, when reopening a closed ticket, the reopen reason.
  const handleQuickStatusChange = (newStatusID) => {
    if (!ticket || Number(ticket.statusID) === Number(newStatusID)) return;
    const fromStatus = statuses.find((s) => Number(s.statusID) === Number(ticket.statusID));
    const toStatus = statuses.find((s) => Number(s.statusID) === Number(newStatusID));
    setStatusComment('');
    setStatusReopenReasonID('');
    setPendingStatus({
      toStatusID: Number(newStatusID),
      toStatusName: toStatus?.statusName || '',
      isReopen: !!fromStatus?.isClosedStatusFlag && !toStatus?.isClosedStatusFlag,
    });
  };

  const confirmStatusChange = async () => {
    if (!pendingStatus) return;
    setChangingStatus(true);
    try {
      await ticketService.changeStatus(ticket.ticketID, {
        ...ticket,
        fromStatusID: ticket.statusID,
        toStatusID: pendingStatus.toStatusID,
        statusComment: statusComment.trim() || undefined,
        reopenReasonID: statusReopenReasonID ? Number(statusReopenReasonID) : ticket.reopenReasonID,
      });
      setPendingStatus(null);
      await loadTicket();
      toast.success('Ticket status updated.');
    } catch (err) {
      console.error('Failed to update ticket status:', err);
      // The API explains rule failures (transition not allowed, missing fields, reopen reason)
      toast.error(err?.response?.data?.message || 'Failed to update ticket status.');
    } finally {
      setChangingStatus(false);
    }
  };

  // Load the ticket + all its related tabs' data.
  useEffect(() => {
    loadTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  const loadTicket = async () => {
    setLoading(true);
    try {
      const [
        ticketRes,
        commentsRes,
        historyRes,
        attachmentsRes,
        assignmentsRes,
        usersRes,
        projectsRes,
        statusesRes,
        severitiesRes,
        typesRes,
        envsRes,
        phasesRes,
        clientOrgsRes,
        reopenReasonsRes
      ] = await Promise.all([
        ticketService.getById(ticketId),
        ticketService.getComments(ticketId),
        ticketService.getHistory(ticketId),
        ticketService.getAttachments(ticketId),
        ticketService.getAssignments(ticketId),
        userService.getAll().catch(() => ({ data: [] })),
        projectService.getAll().catch(() => ({ data: [] })),
        ticketStatusService.getAll().catch(() => ({ data: [] })),
        ticketSeverityService.getAll().catch(() => ({ data: [] })),
        ticketTypeService.getAll().catch(() => ({ data: [] })),
        projectEnvironmentService.getAll().catch(() => ({ data: [] })),
        projectService.getAllPhases().catch(() => ({ data: [] })),
        clientOrgService.getAll().catch(() => ({ data: [] })),
        ticketReopenReasonService.getAll().catch(() => ({ data: [] })),
      ]);

      const allUsers = usersRes.data?.data || usersRes.data || [];
      setUsers(allUsers);

      const rawTicket = ticketRes.data;
      const allProjects = projectsRes.data?.data || projectsRes.data || [];
      const allStatuses = statusesRes.data?.data || statusesRes.data || [];
      setStatuses(allStatuses);
      const allSeverities = severitiesRes.data?.data || severitiesRes.data || [];
      const allTypes = typesRes.data?.data || typesRes.data || [];
      const allEnvs = envsRes.data?.data || envsRes.data || [];
      const allPhases = phasesRes.data?.data || phasesRes.data || [];
      const allClientOrgs = clientOrgsRes.data?.data || clientOrgsRes.data || [];
      const allReopenReasons = reopenReasonsRes.data?.data || reopenReasonsRes.data || [];
      // Only this ticket's project's statuses / reopen reasons can be used
      const projectIdOf = (x) => Number(x.projectID ?? x.projectId);
      if (allStatuses.length > 0) {
        setStatuses(
          allStatuses
            .filter((s) => projectIdOf(s) === Number(rawTicket.projectID))
            .sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0))
        );
      }
      setReopenReasons(allReopenReasons.filter((r) => projectIdOf(r) === Number(rawTicket.projectID)));

      // Fetch Sub-Types for the specific ticket type dynamically
      let subTypes = [];
      if (rawTicket?.ticketTypeID) {
        try {
          const subTypesRes = await ticketTypeService.getSubTypes(rawTicket.ticketTypeID);
          subTypes = subTypesRes.data || [];
        } catch (e) {
          console.warn("Failed to fetch sub-types for type:", rawTicket.ticketTypeID);
        }
      }

      // Find resolved names for the ticket
      const proj = allProjects.find(p => Number(p.projectID || p.projectId) === Number(rawTicket.projectID));
      const status = allStatuses.find(s => Number(s.statusID || s.statusId) === Number(rawTicket.statusID));
      const severity = allSeverities.find(s => Number(s.severityID || s.severityId) === Number(rawTicket.severityID));
      const type = allTypes.find(t => Number(t.ticketTypeID || t.ticketTypeId) === Number(rawTicket.ticketTypeID));
      const env = allEnvs.find(e => Number(e.environmentID || e.environmentId) === Number(rawTicket.environmentID));
      const creator = allUsers.find(u => Number(getUserId(u)) === Number(rawTicket.raisedBy));
      const creatorName = creator ? getUserFullName(creator) : (rawTicket.raisedByName || `User ${rawTicket.raisedBy || ''}`);
      const phase = allPhases.find(ph => Number(ph.phaseID || ph.phaseId) === Number(rawTicket.phaseID));
      const subType = subTypes.find(sub => Number(sub.subTypeID || sub.subTypeId) === Number(rawTicket.ticketSubTypeID));
      const clientOrg = allClientOrgs.find(org => Number(org.clientOrgID || org.clientOrgId) === Number(rawTicket.clientOrgID));
      const reopenReason = allReopenReasons.find(r => Number(r.reopenReasonID || r.reopenReasonId) === Number(rawTicket.reopenReasonID));
      const lastUpdater = allUsers.find(u => Number(getUserId(u)) === Number(rawTicket.lastUpdatedBy));
      const lastUpdaterName = lastUpdater ? getUserFullName(lastUpdater) : `User ${rawTicket.lastUpdatedBy || ''}`;

      const resolvedTicket = {
        ...rawTicket,
        projectName: proj ? proj.projectName : (rawTicket.projectName || '-'),
        projectStatus: proj ? proj.status : '',
        statusName: status ? status.statusName : (rawTicket.statusName || '-'),
        severityName: severity ? severity.severityName : (rawTicket.severityName || '-'),
        typeName: type ? type.typeName : (rawTicket.typeName || '-'),
        subTypeName: subType ? subType.subTypeName : (rawTicket.subTypeName || '-'),
        phaseName: phase ? phase.phaseName : (rawTicket.phaseName || '-'),
        environmentName: env ? env.environmentName : (rawTicket.environmentName || 'Default'),
        raisedByName: creatorName,
        raisedById: rawTicket.raisedBy,
        clientOrgName: clientOrg ? clientOrg.orgName : (rawTicket.clientOrgName || '-'),
        reopenReasonText: reopenReason ? reopenReason.reasonText : (rawTicket.reopenReasonText || '-'),
        lastUpdatedByName: lastUpdaterName,
        lastUpdatedById: rawTicket.lastUpdatedBy,
      };

      setTicket(resolvedTicket);

      // Resolve commenter name from users list
      const resolvedComments = (commentsRes.data || []).map(c => {
        const commenterId = c.commentedBy;
        const commenter = allUsers.find(u => Number(getUserId(u)) === Number(commenterId));
        const commenterName = commenter ? getUserFullName(commenter) : (c.commentedByName || `User ${commenterId}`);
        return {
          ...c,
          commentedByName: commenterId ? `${commenterName} (ID: ${commenterId})` : commenterName,
        };
      });
      setComments(resolvedComments);

      // Resolve history changedBy name
      const resolvedHistory = (historyRes.data || []).map(h => {
        const changerId = h.changedBy;
        const changer = allUsers.find(u => Number(getUserId(u)) === Number(changerId));
        const changerName = changer ? getUserFullName(changer) : (h.changedByName || `User ${changerId}`);
        const fromSt = allStatuses.find(s => Number(s.statusID || s.statusId) === Number(h.fromStatusID));
        const toSt = allStatuses.find(s => Number(s.statusID || s.statusId) === Number(h.toStatusID));
        return {
          ...h,
          changedByName: changerId ? `${changerName} (ID: ${changerId})` : changerName,
          fromStatusName: fromSt ? fromSt.statusName : h.fromStatusName,
          toStatusName: toSt ? toSt.statusName : h.toStatusName,
        };
      });
      setHistory(resolvedHistory);

      setAttachments(attachmentsRes.data || []);

      // Resolve assignee name from users list
      const resolvedAssignments = resolveAssignmentList(assignmentsRes.data || [], allUsers);
      setAssignments(resolvedAssignments);
    } catch (err) {
      toast.error('Failed to load ticket details.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      await ticketService.addComment(ticketId, {
        commentText: newComment,
        isInternalFlag: isInternalComment,
      });
      setNewComment('');
      setIsInternalComment(false);
      const res = await ticketService.getComments(ticketId);
      const allUsersRes = await userService.getAll().catch(() => ({ data: [] }));
      const allUsers = allUsersRes.data?.data || allUsersRes.data || users || [];
      const resolvedComments = (res.data || []).map(c => {
        const commenterId = c.commentedBy;
        const commenter = allUsers.find(u => Number(getUserId(u)) === Number(commenterId));
        const commenterName = commenter ? getUserFullName(commenter) : (c.commentedByName || `User ${commenterId}`);
        return {
          ...c,
          commentedByName: commenterId ? `${commenterName} (ID: ${commenterId})` : commenterName,
        };
      });
      setComments(resolvedComments);
      toast.success('Comment added.');
    } catch (err) {
      toast.error('Failed to add comment.');
    }
  };

  const handleFileSelect = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;

    // Reject files the API would refuse (type / size), with a clear message
    const files = picked.filter((file) => {
      const ext = (file.name.match(/\.[^.]+$/) || [''])[0].toLowerCase();
      if (!ALLOWED_ATTACHMENT_EXTENSIONS.includes(ext)) {
        toast.error(`'${file.name}' is not an allowed file type.`);
        return false;
      }
      if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
        toast.error(`'${file.name}' is larger than ${MAX_ATTACHMENT_MB} MB.`);
        return false;
      }
      return true;
    });
    e.target.value = '';
    if (files.length === 0) return;

    const newSelected = files.map(file => ({
      file,
      name: file.name,
      sizeKB: Math.round(file.size / 1024),
      url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));

    setSelectedFiles(prev => [...prev, ...newSelected]);
    e.target.value = '';
  };

  const handleUploadSelected = async () => {
    if (selectedFiles.length === 0) return;
    try {
      const filesToUpload = selectedFiles.map(f => f.file);
      await ticketService.uploadAttachment(ticketId, filesToUpload);

      selectedFiles.forEach(f => {
        if (f.url) URL.revokeObjectURL(f.url);
      });
      setSelectedFiles([]);

      const res = await ticketService.getAttachments(ticketId);
      setAttachments(res.data || []);
      toast.success('Attachments saved successfully.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save attachments.');
    }
  };

  const handleCancelSelection = () => {
    selectedFiles.forEach(f => {
      if (f.url) URL.revokeObjectURL(f.url);
    });
    setSelectedFiles([]);
  };

  const handleRemovePendingFile = (index) => {
    setSelectedFiles(prev => {
      const copy = [...prev];
      const removed = copy.splice(index, 1)[0];
      if (removed && removed.url) {
        URL.revokeObjectURL(removed.url);
      }
      return copy;
    });
  };

  const triggerDeleteConfirm = (attachmentID) => {
    setDeleteConfirmId(attachmentID);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await ticketService.removeAttachment(ticketId, deleteConfirmId);
      const res = await ticketService.getAttachments(ticketId);
      setAttachments(res.data || []);
      toast.success('Attachment deleted.');
    } catch (err) {
      toast.error('Failed to delete attachment.');
    } finally {
      setDeleteConfirmId(null);
    }
  };

  if (loading) return <Loader label="Loading ticket..." />;
  if (!ticket) return <p>Ticket not found.</p>;

  // Archived / Closed projects are read-only (the API rejects ticket edits)
  const isProjectReadOnly = ['archived', 'closed'].includes(String(ticket?.projectStatus || '').toLowerCase());

  const visibleComments = hasRole('Client') ? comments.filter((c) => !c.isInternalFlag) : comments;

  return (
    <div className="ticket-detail-container">
      {/* ---------- Header ---------- */}
      <div className="page-header" style={{ padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '800', margin: '0 0 8px 0' }}>
            {ticket.ticketNumber} <span style={{ color: 'var(--color-text-muted)', fontWeight: '400', fontSize: '24px' }}>—</span> {ticket.title}
          </h1>
          <div className="ticket-meta" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <StatusBadge value={ticket.statusName} />
              {!hasRole('Client') && !isProjectReadOnly && statuses.length > 0 && (
                <select
                  value={ticket.statusID || ''}
                  onChange={(e) => handleQuickStatusChange(e.target.value)}
                  disabled={changingStatus}
                  style={{
                    padding: '2px 8px',
                    fontSize: '11.5px',
                    fontWeight: '700',
                    borderRadius: '4px',
                    border: '1.5px solid var(--afix-orange, #f97316)',
                    background: '#fff',
                    color: '#ea580c',
                    cursor: 'pointer',
                    outline: 'none'
                  }}
                  title="Change ticket status (automatically records status history transition)"
                >
                  {statuses.map((s) => (
                    <option key={s.statusID} value={s.statusID}>
                      Move to: {s.statusName}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <StatusBadge value={ticket.severityName} />
            <span className="badge badge-info" style={{ textTransform: 'none', background: 'var(--color-info-bg)', color: 'var(--color-info)', borderColor: 'rgba(59, 130, 246, 0.15)' }}>
              🏷️ {ticket.typeName}
            </span>
            {ticket.subTypeName && ticket.subTypeName !== '-' && (
              <span className="badge badge-success" style={{ textTransform: 'none', background: 'rgba(16, 185, 129, 0.08)', color: 'var(--color-success)', borderColor: 'rgba(16, 185, 129, 0.15)' }}>
                🔹 {ticket.subTypeName}
              </span>
            )}
          </div>
        </div>

        {/* ---------- Back Button ---------- */}
        <button
          onClick={() => navigate('/tickets')}
          className="btn btn-secondary"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: '700',
            fontSize: '13.5px',
            padding: '10px 18px',
            borderRadius: '30px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            outline: 'none',
            alignSelf: 'center'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.12)';
            e.currentTarget.style.borderColor = 'var(--afix-orange)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
            e.currentTarget.style.borderColor = 'var(--color-border-solid)';
          }}
        >
          <span>←</span>
          <span>Back to Tickets</span>
        </button>
      </div>

      <div className="ticket-detail-grid">
        {/* ---------- Left: description + tabs ---------- */}
        <div>
          <div className="card" style={{ marginBottom: 24, padding: '28px' }}>
            <div className="sidebar-panel-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
              <span>📝</span> Description
            </div>
            <p style={{ fontSize: '14.5px', lineHeight: '1.6', color: 'var(--color-text)', marginTop: '8px', marginBottom: '20px', whiteSpace: 'pre-wrap' }}>
              {ticket.description}
            </p>

            {ticket.detailedDescription && (
              <div style={{ marginTop: '24px', borderTop: '1px solid var(--color-border)', paddingTop: '20px' }}>
                <div className="sidebar-panel-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <span>🔍</span> Detailed Description
                </div>
                <div style={{
                  marginTop: '10px',
                  background: 'rgba(30, 41, 59, 0.03)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  borderRadius: 'var(--radius-md)',
                  padding: '16px 20px',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: 'var(--color-text)',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit'
                }}>
                  {ticket.detailedDescription}
                </div>
              </div>
            )}

            {/* Inline ticket images (Gallery) */}
            {attachments.filter(a => isImageFile(a.fileName, a.fileType)).length > 0 && (
              <div style={{ marginTop: '24px', borderTop: '1px solid var(--color-border)', paddingTop: '20px' }}>
                <div className="sidebar-panel-title" style={{ marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <span>🖼️</span> Ticket Attachments
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '14px' }}>
                  {attachments.filter(a => isImageFile(a.fileName, a.fileType)).map((a) => (
                    <div
                      key={a.attachmentID}
                      style={{ position: 'relative', overflow: 'hidden', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}
                      className="gallery-item-wrapper"
                    >
                      <a
                        key={a.attachmentID}
                        href={getAttachmentUrl(a.filePath)}
                        target="_blank"
                        rel="noreferrer"
                        style={{ display: 'block' }}
                        title={a.fileName}
                      >
                        <img
                          src={getAttachmentUrl(a.filePath)}
                          alt={a.fileName}
                          style={{
                            width: '100%',
                            height: '100px',
                            objectFit: 'cover',
                            display: 'block',
                            transition: 'transform 0.3s ease'
                          }}
                          className="gallery-img"
                        />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {isProjectReadOnly && (
            <div
              className="card"
              style={{ marginBottom: 20, borderLeft: '4px solid #f59e0b', background: 'rgba(245, 158, 11, 0.06)' }}
            >
              <strong>Project is {ticket.projectStatus}.</strong> This ticket is read-only - its status and details
              can no longer be changed.
            </div>
          )}

          <TicketClientAnswers ticketID={ticket.ticketID} />

          <div className="card" style={{ padding: '28px' }}>
            <div className="tabs">
              {TABS.map((tab) => {
                let count = 0;
                if (tab === 'Comments') count = visibleComments.length;
                if (tab === 'History') count = history.length;
                if (tab === 'Attachments') count = attachments.length;

                return (
                  <button
                    key={tab}
                    className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <span>{tab}</span>
                    <span style={{
                      fontSize: '11px',
                      background: activeTab === tab ? 'var(--afix-orange)' : 'var(--color-bg)',
                      color: activeTab === tab ? '#fff' : 'var(--color-text-muted)',
                      padding: '2px 7px',
                      borderRadius: '10px',
                      fontWeight: '700',
                      transition: 'all 0.2s'
                    }}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* ---------- Comments tab ---------- */}
            {activeTab === 'Comments' && (
              <div>
                <form onSubmit={handleAddComment} style={{ marginBottom: 24 }}>
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <textarea
                      rows={3}
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: 'var(--radius-md)',
                        border: '1.5px solid var(--color-border-solid)',
                        fontSize: '14px',
                        outline: 'none',
                        background: 'rgba(255, 255, 255, 0.4)',
                        transition: 'all 0.25s'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--afix-orange)';
                        e.target.style.background = '#fff';
                        e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.15)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--color-border-solid)';
                        e.target.style.background = 'rgba(255, 255, 255, 0.4)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {/* Team-only comments are only offered to internal roles, not Clients */}
                    {!hasRole('Client') && (
                      <label style={{ fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '600', color: 'var(--color-text-muted)' }}>
                        <input
                          type="checkbox"
                          checked={isInternalComment}
                          onChange={(e) => setIsInternalComment(e.target.checked)}
                          style={{
                            cursor: 'pointer',
                            accentColor: 'var(--afix-orange)',
                            width: '15px',
                            height: '15px'
                          }}
                        />
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>🔒 Internal comment (Team-only)</span>
                      </label>
                    )}
                    <button type="submit" className="btn btn-primary" style={{ padding: '8px 20px', borderRadius: 'var(--radius-sm)' }}>
                      Post Comment
                    </button>
                  </div>
                </form>

                {visibleComments.length === 0 ? (
                  <p className="text-muted" style={{ textAlign: 'center', padding: '24px 0' }}>No comments yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {visibleComments.map((c) => {
                      const userInitials = c.commentedByName
                        ? c.commentedByName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                        : 'U';
                      return (
                        <div
                          key={c.commentID}
                          style={{
                            background: c.isInternalFlag ? 'rgba(245, 158, 11, 0.04)' : 'rgba(255, 255, 255, 0.4)',
                            border: c.isInternalFlag ? '1px dashed var(--color-warning)' : '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-md)',
                            padding: '16px 20px',
                            display: 'flex',
                            gap: '14px',
                            transition: 'all 0.2s'
                          }}
                          className="comment-item-card"
                        >
                          <div style={{
                            width: '38px',
                            height: '38px',
                            borderRadius: '50%',
                            background: c.isInternalFlag ? 'var(--color-warning)' : 'var(--afix-gradient-primary)',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '13px',
                            fontWeight: '800',
                            flexShrink: 0,
                            boxShadow: c.isInternalFlag ? 'var(--glow-warning)' : 'var(--glow-primary)'
                          }}>
                            {userInitials}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                              <span style={{ fontWeight: '700', fontSize: '13.5px', color: 'var(--color-text)' }}>
                                {c.commentedByName}
                                {c.isInternalFlag && (
                                  <span className="badge badge-warning" style={{ fontSize: '9px', padding: '2px 6px', marginLeft: '8px', verticalAlign: 'middle', textTransform: 'none' }}>
                                    🔒 Internal
                                  </span>
                                )}
                              </span>
                              <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', fontWeight: '600' }}>
                                {formatDate(c.createdDttm)}
                              </span>
                            </div>
                            <div style={{ fontSize: '13.8px', color: 'var(--color-text)', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                              {c.commentText}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ---------- History tab (Requirement #14) ---------- */}
            {activeTab === 'History' && (
              <div>
                {history.length === 0 ? (
                  <p className="text-muted" style={{ textAlign: 'center', padding: '24px 0' }}>No status history yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0px', paddingLeft: '8px', position: 'relative' }}>
                    {/* Vertical line indicator */}
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      bottom: '12px',
                      left: '12px',
                      width: '2px',
                      background: 'rgba(249, 115, 22, 0.15)',
                      zIndex: 1
                    }} />

                    {history.map((h, index) => (
                      <div
                        className="timeline-row"
                        key={h.historyID}
                        style={{
                          display: 'flex',
                          gap: '20px',
                          padding: '16px 0',
                          position: 'relative',
                          zIndex: 2
                        }}
                      >
                        {/* Dot indicator */}
                        <div style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          background: 'var(--afix-orange)',
                          marginTop: '6px',
                          boxShadow: '0 0 0 4px var(--afix-orange-light)',
                          flexShrink: 0,
                          marginLeft: '8px'
                        }} />

                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                            <span className="badge" style={{ background: 'var(--color-info-bg)', color: 'var(--color-info)', fontSize: '11px', textTransform: 'none', border: '1px solid rgba(59,130,246,0.1)' }}>
                              {h.fromStatusName || 'Created'}
                            </span>
                            <span style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>→</span>
                            <span className="badge badge-success" style={{ fontSize: '11px', textTransform: 'none' }}>
                              {h.toStatusName}
                            </span>
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', fontWeight: '600', marginTop: '4px' }}>
                            Changed by <strong style={{ color: 'var(--color-text)' }}>{h.changedByName}</strong> on {formatDate(h.changedDttm)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ---------- Attachments tab ---------- */}
            {activeTab === 'Links' && (
              <TicketLinksPanel ticket={ticket} canEdit={!hasRole('Client')} />
            )}

            {activeTab === 'Attachments' && (
              <div>
                <div style={{ marginBottom: 24 }}>
                  <input
                    type="file"
                    id="attachment-file-input"
                    onChange={handleFileSelect}
                    accept={ALLOWED_ATTACHMENT_EXTENSIONS.join(',')}
                    style={{ display: 'none' }}
                    multiple
                  />
                  <label
                    htmlFor="attachment-file-input"
                    className="btn btn-outline"
                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', fontWeight: '700', borderRadius: 'var(--radius-sm)' }}
                  >
                    📎 Select Files to Add
                  </label>
                </div>

                {/* Pending Staged Files Section */}
                {selectedFiles.length > 0 && (
                  <div style={{
                    background: 'rgba(255, 137, 6, 0.03)',
                    border: '1.5px dashed var(--afix-orange)',
                    borderRadius: 'var(--radius-md)',
                    padding: '20px',
                    marginBottom: '28px'
                  }}>
                    <h3 style={{ fontSize: '14px', fontWeight: '800', marginBottom: '14px', color: 'var(--afix-orange-dark)' }}>
                      Pending Selection ({selectedFiles.length})
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '14px', marginBottom: '20px' }}>
                      {selectedFiles.map((f, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '10px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-md)',
                            position: 'relative',
                            background: 'rgba(255, 255, 255, 0.8)'
                          }}
                        >
                          <button
                            onClick={() => handleRemovePendingFile(idx)}
                            style={{
                              position: 'absolute',
                              top: '-6px',
                              right: '-6px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '22px',
                              height: '22px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              fontSize: '11px',
                              zIndex: 15,
                              boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                            }}
                            title="Remove from selection"
                          >
                            ✕
                          </button>
                          {f.url ? (
                            <img
                              src={f.url}
                              alt={f.name}
                              style={{
                                width: '100%',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: 'var(--radius-sm)'
                              }}
                            />
                          ) : (
                            <div style={{ height: '80px', display: 'flex', alignItems: 'center', fontSize: '36px' }}>📄</div>
                          )}
                          <div style={{ fontSize: '11px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%', textAlign: 'center', fontWeight: '700', color: 'var(--color-text)' }} title={f.name}>
                            {f.name}
                          </div>
                          <span style={{ fontSize: '10px', color: 'var(--color-text-muted)', fontWeight: '600' }}>{f.sizeKB} KB</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={handleUploadSelected}
                        className="btn btn-primary"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: '700', padding: '9px 18px' }}
                      >
                        💾 Save Attachments
                      </button>
                      <button
                        onClick={handleCancelSelection}
                        className="btn btn-secondary"
                        style={{ fontWeight: '700', padding: '9px 18px' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {attachments.length === 0 ? (
                  <p className="text-muted" style={{ textAlign: 'center', padding: '24px 0' }}>No attachments yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Render Image Gallery */}
                    {attachments.some(a => isImageFile(a.fileName, a.fileType)) && (
                      <div>
                        <h3 style={{ fontSize: '13.5px', fontWeight: '800', marginBottom: '12px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Image Gallery
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '14px' }}>
                          {attachments.filter(a => isImageFile(a.fileName, a.fileType)).map((a) => (
                            <div
                              key={a.attachmentID}
                              style={{
                                padding: '10px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '8px',
                                border: '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-md)',
                                overflow: 'hidden',
                                position: 'relative',
                                background: 'rgba(255, 255, 255, 0.4)'
                              }}
                            >
                              <button
                                onClick={() => triggerDeleteConfirm(a.attachmentID)}
                                style={{
                                  position: 'absolute',
                                  top: '8px',
                                  right: '8px',
                                  background: 'rgba(239, 68, 68, 0.95)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '50%',
                                  width: '26px',
                                  height: '26px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                                  zIndex: 10,
                                  fontWeight: 'bold',
                                  fontSize: '11px'
                                }}
                                title="Delete Attachment"
                              >
                                🗑️
                              </button>

                              <a href={getAttachmentUrl(a.filePath)} target="_blank" rel="noreferrer" style={{ width: '100%' }}>
                                <img
                                  src={getAttachmentUrl(a.filePath)}
                                  alt={a.fileName}
                                  style={{
                                    width: '100%',
                                    height: '110px',
                                    objectFit: 'cover',
                                    borderRadius: 'var(--radius-sm)',
                                    display: 'block'
                                  }}
                                />
                              </a>
                              <div style={{ width: '100%', fontSize: '11.5px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', textAlign: 'center', fontWeight: '700', color: 'var(--color-text)' }} title={a.fileName}>
                                {a.fileName}
                              </div>
                              <span style={{ fontSize: '10px', color: 'var(--color-text-muted)', fontWeight: '600' }}>
                                {a.fileSizeKB} KB
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Render All/Other Files List */}
                    <div>
                      <h3 style={{ fontSize: '13.5px', fontWeight: '800', marginBottom: '12px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        All Files ({attachments.length})
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {attachments.map((a) => {
                          const isImg = isImageFile(a.fileName, a.fileType);
                          return (
                            <div
                              key={a.attachmentID}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 18px',
                                background: 'rgba(255, 255, 255, 0.4)',
                                border: '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-md)'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '24px' }}>{isImg ? '🖼️' : '📄'}</span>
                                <div>
                                  <a href={getAttachmentUrl(a.filePath)} target="_blank" rel="noreferrer" style={{ fontWeight: '700', color: 'var(--afix-orange)', textDecoration: 'none', fontSize: '13.5px' }}>
                                    {a.fileName}
                                  </a>
                                  <div style={{ fontSize: '11.5px', color: 'var(--color-text-muted)', fontWeight: '600', marginTop: '2px' }}>
                                    {a.fileSizeKB} KB • Virus Scan: <strong style={{ color: a.virusScanStatus === 'Clean' ? '#10b981' : '#f59e0b' }}>{a.virusScanStatus}</strong>
                                  </div>
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <a
                                  href={getAttachmentUrl(a.filePath)}
                                  download={a.fileName}
                                  className="btn btn-secondary btn-sm"
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ padding: '6px 12px', fontSize: '11.5px', display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: '700', borderRadius: 'var(--radius-sm)' }}
                                >
                                  ⬇️ Download
                                </a>
                                <button
                                  onClick={() => triggerDeleteConfirm(a.attachmentID)}
                                  className="btn btn-danger btn-sm"
                                  style={{ padding: '6px 12px', fontSize: '11.5px', border: 'none', background: '#ef4444', color: 'white', fontWeight: '700', borderRadius: 'var(--radius-sm)' }}
                                >
                                  🗑️ Delete
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ---------- Right: ticket meta sidebar ---------- */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignSelf: 'start' }}>
          {/* Card 1: Ticket Classification */}
          <div className="card" style={{ padding: '24px' }}>
            <div className="sidebar-panel-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
              <span>🏢</span> Classification
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13.5px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-text-muted)', fontWeight: '600' }}>Project:</span>
                <span style={{ fontWeight: '700', color: 'var(--color-text)' }}>{ticket.projectName}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-text-muted)', fontWeight: '600' }}>Phase:</span>
                <span style={{ fontWeight: '700', color: 'var(--color-text)' }}>{ticket.phaseName}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-text-muted)', fontWeight: '600' }}>Environment:</span>
                <span style={{ fontWeight: '700', color: 'var(--color-text)' }}>{ticket.environmentName}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-text-muted)', fontWeight: '600' }}>Sub-Type:</span>
                <span style={{ fontWeight: '700', color: 'var(--color-text)' }}>{ticket.subTypeName}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-text-muted)', fontWeight: '600' }}>Client Org:</span>
                <span style={{ fontWeight: '700', color: 'var(--color-text)' }}>{ticket.clientOrgName}</span>
              </div>
            </div>
          </div>

          {/* Card 2: Associations (PEOPLE) */}
          <div className="card" style={{ padding: '24px' }}>
            <div className="sidebar-panel-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
              <span>👥</span> People
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '13.5px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'var(--color-text-muted)', fontWeight: '600' }}>Raised By:</span>
                <span style={{ fontWeight: '700', color: 'var(--color-text)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'var(--afix-gradient-primary)', color: '#fff', fontSize: '10px', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {(ticket.raisedByName || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  {ticket.raisedByName || 'User'} {ticket.raisedById || ticket.raisedBy ? `(ID: ${ticket.raisedById || ticket.raisedBy})` : ''}
                </span>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <span style={{ color: 'var(--color-text-muted)', fontWeight: '600' }}>Assignees:</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                    {assignments.filter(a => a.isCurrentFlag).length > 0 ? (
                      assignments.filter(a => a.isCurrentFlag).map(a => (
                        <span
                          key={a.assignmentID}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: 'rgba(249, 115, 22, 0.1)',
                            color: 'var(--afix-orange-dark, #ea580c)',
                            border: '1px solid rgba(249, 115, 22, 0.25)',
                            padding: '3px 9px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '700'
                          }}
                        >
                          👤 {a.userName} {a.userID ? `(ID: ${a.userID})` : ''}
                          {!hasRole('Client') && (
                            <button
                              type="button"
                              onClick={() => handleUnassignUser(a.assignmentID)}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                color: '#ef4444',
                                cursor: 'pointer',
                                padding: '0 2px',
                                fontSize: '11px',
                                fontWeight: '800',
                                lineHeight: 1
                              }}
                              title="Unassign user"
                            >
                              ✕
                            </button>
                          )}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: 'var(--color-text-muted)', fontWeight: '500' }}>Unassigned</span>
                    )}
                  </div>
                </div>

                {/* Inline Assignee Selector with Dropdown and Assign Button */}
                {!hasRole('Client') && (
                  <div style={{
                    background: 'rgba(249, 115, 22, 0.04)',
                    border: '1px solid rgba(249, 115, 22, 0.2)',
                    borderRadius: 'var(--radius-sm, 6px)',
                    padding: '10px',
                    marginTop: '6px'
                  }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--afix-orange-dark, #ea580c)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      Assign Ticket to User
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <select
                        value={selectedAssignUserId}
                        onChange={(e) => setSelectedAssignUserId(e.target.value)}
                        disabled={assigning}
                        style={{
                          flex: 1,
                          padding: '6px 8px',
                          fontSize: '12px',
                          borderRadius: '4px',
                          border: '1px solid var(--color-border)',
                          background: 'var(--color-bg, #ffffff)',
                          color: 'var(--color-text)',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">-- Select User --</option>
                        {users.map((u) => {
                          const uid = getUserId(u);
                          const uname = getUserFullName(u);
                          const isAssigned = assignments.some(a => a.isCurrentFlag && Number(a.userID || a.userId) === Number(uid));
                          return (
                            <option key={uid} value={uid}>
                              {uname} (ID: {uid}){u.email ? ` - ${u.email}` : ''} {u.roleName || u.role ? `[${u.roleName || u.role}]` : ''} {isAssigned ? '✓ (Current Assignee)' : ''}
                            </option>
                          );
                        })}
                      </select>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleAssignUser}
                        disabled={assigning || !selectedAssignUserId}
                        style={{ padding: '6px 12px', fontSize: '11.5px', fontWeight: '700', whiteSpace: 'nowrap' }}
                      >
                        {assigning ? '...' : 'Assign'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Card 3: Date & Updates */}
          <div className="card" style={{ padding: '24px' }}>
            <div className="sidebar-panel-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
              <span>📅</span> Timeline & Activity
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13.5px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-text-muted)', fontWeight: '600' }}>Created:</span>
                <span style={{ fontWeight: '700', color: 'var(--color-text)' }}>
                  {formatDate(ticket.createDate)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-text-muted)', fontWeight: '600' }}>Last Updated:</span>
                <span style={{ fontWeight: '700', color: 'var(--color-text)' }}>
                  {formatDate(ticket.lastUpdateDttm)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-text-muted)', fontWeight: '600' }}>Updated By:</span>
                <span style={{ fontWeight: '700', color: 'var(--color-text)' }}>
                  {ticket.lastUpdatedByName || 'User'} {ticket.lastUpdatedById || ticket.lastUpdatedBy ? `(ID: ${ticket.lastUpdatedById || ticket.lastUpdatedBy})` : ''}
                </span>
              </div>
              {(ticket.resolvedDate || ticket.statusName === 'Resolved' || ticket.statusName === 'Closed') && (
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--color-border)', paddingTop: '10px', marginTop: '2px' }}>
                  <span style={{ color: 'var(--color-success)', fontWeight: '700' }}>
                    {ticket.statusName === 'Closed' ? 'Closed:' : 'Resolved:'}
                  </span>
                  <span style={{ fontWeight: '700', color: 'var(--color-success)' }}>
                    {formatDate(ticket.resolvedDate || (ticket.statusName === 'Closed' || ticket.statusName === 'Resolved' ? ticket.lastUpdateDttm : null))}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Card 4: Reopen Information (if applicable) */}
          {(ticket.reopenCount > 0 || (ticket.reopenReasonText && ticket.reopenReasonText !== '-')) && (
            <div className="card" style={{ padding: '24px', background: 'rgba(239, 68, 68, 0.02)', borderColor: 'rgba(239, 68, 68, 0.15)' }}>
              <div className="sidebar-panel-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: 'var(--color-danger)', borderBottomColor: 'rgba(239, 68, 68, 0.15)' }}>
                <span>🔄</span> Reopen Information
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13.5px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--color-text-muted)', fontWeight: '600' }}>Reopen Count:</span>
                  <span style={{ fontWeight: '700', color: 'var(--color-danger)' }}>{ticket.reopenCount} time(s)</span>
                </div>
                {ticket.reopenReasonText && ticket.reopenReasonText !== '-' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ color: 'var(--color-text-muted)', fontWeight: '600' }}>Reopen Reason:</span>
                    <span style={{ fontWeight: '600', color: 'var(--color-text)', background: 'rgba(0,0,0,0.02)', padding: '6px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border-solid)', fontSize: '12.5px', marginTop: '2px' }}>
                      {ticket.reopenReasonText}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Custom Deletion Confirmation Modal */}
      {deleteConfirmId && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: 'var(--color-bg-card, #ffffff)',
            borderRadius: 'var(--radius-md, 12px)',
            padding: '24px',
            width: '90%',
            maxWidth: '400px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            border: '1px solid var(--color-border)'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '800', color: 'var(--color-text)' }}>
              Confirm Deletion
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '14.5px', color: 'var(--color-text-muted)', lineHeight: '1.5' }}>
              Are you sure you want to delete this attachment? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="btn btn-secondary"
                style={{ fontWeight: '700' }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="btn btn-danger"
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  fontWeight: '700',
                  padding: '8px 16px',
                  borderRadius: 'var(--radius-sm)'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ---------- Status change dialog ---------- */}
      {pendingStatus && (
        <Modal title={`Move to: ${pendingStatus.toStatusName}`} onClose={() => setPendingStatus(null)} size="small">
          {pendingStatus.isReopen && (
            <div className="form-group">
              <label htmlFor="reopenReason">Reopen Reason</label>
              <select
                id="reopenReason"
                value={statusReopenReasonID}
                onChange={(e) => setStatusReopenReasonID(e.target.value)}
              >
                <option value="">-- Select a reason --</option>
                {reopenReasons.map((r) => (
                  <option key={r.reopenReasonID} value={r.reopenReasonID}>
                    {r.reasonText}
                    {r.isMandatoryFlag ? ' *' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="form-group">
            <label htmlFor="statusComment">Notes / Resolution Notes (optional unless required)</label>
            <textarea
              id="statusComment"
              rows={3}
              value={statusComment}
              onChange={(e) => setStatusComment(e.target.value)}
              placeholder="Saved on the ticket as a comment"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="btn btn-outline" onClick={() => setPendingStatus(null)} disabled={changingStatus}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={confirmStatusChange} disabled={changingStatus}>
              {changingStatus ? 'Saving...' : 'Change Status'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}